import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  CommercialOrderSource,
  CartItem,
  PosCategory,
  PosProduct,
  PosProductAddon,
  PosTheme,
  PosThemeMode,
  RecentPosOrder,
  WorkspaceSummary,
} from '@adminpages/domain-models';
import {
  CashScope,
  PosApiService,
  PosAuthService,
  PosCashRegister,
  PosCashMovementPayload,
  PosCashSession,
  PosCloseCashSessionPayload,
  PosClient,
  PosCreateClientPayload,
  PosCreateOrderPayload,
  PosOrderCustomerPayload,
  PosOpenCashSessionPayload,
} from '@adminpages/pos-api';
import {
  mockCategories,
  mockProducts,
  mockRecentOrders,
  mockWorkspaces,
} from '@adminpages/mock-data';
import { applyPosTheme, formatMoney, posThemes } from '@adminpages/pos-theme';
import { clearTokenOnUnauthorized, selectRestoredWorkspace } from './session-restoration';

const themeModeStorageKey = 'adminpages.pos.themeMode';

@Injectable({ providedIn: 'root' })
export class PosCartService {
  readonly cart = signal<CartItem[]>([]);

  readonly subtotal = computed(() =>
    this.cart().reduce((total, item) => total + this.itemTotal(item), 0),
  );

  readonly count = computed(() =>
    this.cart().reduce((total, item) => total + item.quantity, 0),
  );

  add(product: PosProduct, quantity = 1, addons: PosProductAddon[] = []): void {
    const addonIds = addons.map((addon) => addon.id).sort().join(',');
    const existingIndex = this.cart().findIndex((item) => {
      const itemAddonIds = item.addons.map((addon) => addon.id).sort().join(',');
      return item.product.id === product.id && itemAddonIds === addonIds;
    });

    if (existingIndex >= 0) {
      const next = [...this.cart()];
      next[existingIndex] = {
        ...next[existingIndex],
        quantity: next[existingIndex].quantity + quantity,
      };
      this.cart.set(next);
      return;
    }

    this.cart.update((items) => [
      ...items,
      {
        lineId: crypto.randomUUID(),
        product,
        quantity,
        addons: addons.map((addon) => ({
          id: addon.id,
          name: addon.name,
          priceDelta: addon.priceDelta,
        })),
      },
    ]);
  }

  increment(lineId: string): void {
    this.cart.update((items) =>
      items.map((item) =>
        item.lineId === lineId ? { ...item, quantity: item.quantity + 1 } : item,
      ),
    );
  }

  decrement(lineId: string): void {
    this.cart.update((items) =>
      items
        .map((item) =>
          item.lineId === lineId
            ? { ...item, quantity: Math.max(0, item.quantity - 1) }
            : item,
        )
        .filter((item) => item.quantity > 0),
    );
  }

  remove(lineId: string): void {
    this.cart.update((items) => items.filter((item) => item.lineId !== lineId));
  }

  clear(): void {
    this.cart.set([]);
  }

  itemTotal(item: CartItem): number {
    const addonTotal = item.addons.reduce((total, addon) => total + addon.priceDelta, 0);

    return item.quantity * (item.product.price + addonTotal);
  }

  toOrderItems(): PosCreateOrderPayload['items'] {
    return this.cart().map((item) => ({
      product_id: item.product.id,
      product_uuid: item.product.uuid,
      slug: item.product.slug,
      sku: item.product.sku,
      quantity: item.quantity,
      selected_addon_ids: item.addons.map((addon) => addon.id),
    }));
  }
}

@Injectable({ providedIn: 'root' })
export class PosWorkspaceService {
  readonly selectedWorkspace = signal<WorkspaceSummary>(mockWorkspaces[0]);
  readonly themeMode = signal<PosThemeMode>(this.loadThemeMode());
  readonly activeTheme = computed(() => this.themeForWorkspace(this.selectedWorkspace()));

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)');
    systemTheme.addEventListener('change', () => {
      if (this.themeMode() === 'system') {
        this.applyTheme();
      }
    });
  }

  select(workspace: WorkspaceSummary): void {
    this.selectedWorkspace.set(workspace);
    this.applyTheme();
  }

  setThemeMode(mode: PosThemeMode): void {
    this.themeMode.set(mode);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(themeModeStorageKey, mode);
    }

    this.applyTheme();
  }

  applyTheme(): void {
    applyPosTheme(this.activeTheme(), this.themeMode());
  }

  themeForWorkspace(workspace: WorkspaceSummary): PosTheme {
    const fallback = posThemes[workspace.themeKey] ?? posThemes.default;
    const settings = workspace.posSettings;

    if (!settings) {
      return fallback;
    }

    return {
      key: workspace.themeKey,
      name: settings.posName || workspace.name,
      primaryColor: settings.colors.primary,
      accentColor: settings.colors.accent,
      backgroundColor: settings.colors.background,
      surfaceColor: settings.colors.surface,
      textColor: settings.colors.text,
      mutedColor: settings.colors.muted,
      borderColor: settings.colors.border,
      shadowColor: settings.colors.shadow,
      inputColor: settings.colors.input,
      dark: {
        backgroundColor: settings.darkColors.background,
        surfaceColor: settings.darkColors.surface,
        textColor: settings.darkColors.text,
        mutedColor: settings.darkColors.muted,
        borderColor: settings.darkColors.border,
        shadowColor: settings.darkColors.shadow,
        inputColor: settings.darkColors.input,
      },
    };
  }

  private loadThemeMode(): PosThemeMode {
    if (typeof window === 'undefined') {
      return 'light';
    }

    const value = window.localStorage.getItem(themeModeStorageKey);

    return value === 'dark' || value === 'system' || value === 'light' ? value : 'light';
  }
}

@Injectable({ providedIn: 'root' })
export class PosStoreService {
  private readonly api = inject(PosApiService);
  private readonly auth = inject(PosAuthService);
  private readonly router = inject(Router);
  private readonly cartService = inject(PosCartService);
  private readonly workspaceService = inject(PosWorkspaceService);
  private sessionRestore: Promise<void> | null = null;
  private sessionInitialized = false;

  readonly workspaces = signal<WorkspaceSummary[]>(mockWorkspaces);
  readonly categories = signal<PosCategory[]>(this.withTodos(mockCategories));
  readonly products = signal<PosProduct[]>(mockProducts);
  readonly recentOrders = signal<RecentPosOrder[]>(mockRecentOrders);
  readonly recentSources = signal<CommercialOrderSource[]>(['pos']);
  readonly activeCategory = signal('todos');
  readonly searchQuery = signal('');
  readonly lastOrder = signal<RecentPosOrder | null>(null);
  readonly authLoading = signal(false);
  readonly logoutLoading = signal(false);
  readonly logoutErrorMessage = signal<string | null>(null);
  readonly workspacesLoading = signal(false);
  readonly catalogLoading = signal(false);
  readonly checkoutLoading = signal(false);
  readonly cashLoading = signal(false);
  readonly cashHistoryLoading = signal(false);
  readonly cashRegistersLoading = signal(false);
  readonly recentLoading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly cashSession = signal<PosCashSession | null>(null);
  readonly cashHistory = signal<PosCashSession[]>([]);
  readonly cashRegisters = signal<PosCashRegister[]>([]);
  readonly selectedCashRegister = signal<PosCashRegister | null>(null);
  readonly cashScope = signal<CashScope>('pos');

  readonly selectedWorkspace = this.workspaceService.selectedWorkspace;
  readonly themeMode = this.workspaceService.themeMode;
  readonly activeTheme = this.workspaceService.activeTheme;
  readonly cart = this.cartService.cart;
  readonly cartSubtotal = this.cartService.subtotal;
  readonly cartCount = this.cartService.count;
  readonly cashRegisterReady = computed(() => !this.auth.token() || Boolean(this.selectedCashRegister()));

  readonly visibleProducts = computed(() => {
    const category = this.activeCategory();
    const query = this.searchQuery().trim().toLowerCase();
    const selectedWorkspace = this.selectedWorkspace();
    const usingMockData = !this.api.hasToken();

    return this.products().filter((product) => {
      const matchesWorkspace = !usingMockData
        ? true
        : selectedWorkspace.themeKey === 'taly'
          ? product.categorySlug === 'comida-rapida'
          : product.categorySlug !== 'comida-rapida';
      const matchesCategory = category === 'todos' || product.categorySlug === category;
      const matchesQuery =
        query === '' ||
        product.name.toLowerCase().includes(query) ||
        product.sku.toLowerCase().includes(query) ||
        (product.barcode ?? '').toLowerCase().includes(query);

      return matchesWorkspace && matchesCategory && matchesQuery;
    });
  });

  applyTheme(): void {
    this.workspaceService.applyTheme();
  }

  async login(email: string, password: string, deviceName: string): Promise<void> {
    await this.api.loadRuntimeConfig();

    this.errorMessage.set(null);
    this.authLoading.set(true);

    try {
      const response = await this.api.login({
        email,
        password,
        deviceName,
        workspaceId: this.auth.loadWorkspaceId() ?? undefined,
      });
      if (response.cashRegister) {
        this.auth.setCashRegisterId(response.workspace.id, response.cashRegister.id);
      }
      this.setWorkspace(response.workspace, true);
      await this.loadWorkspaces();
      await this.refreshWorkspaceData(response.workspace.id);
      this.sessionInitialized = true;
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      throw error;
    } finally {
      this.authLoading.set(false);
    }
  }

  async logout(): Promise<void> {
    if (this.logoutLoading()) {
      return;
    }

    this.logoutErrorMessage.set(null);
    this.logoutLoading.set(true);

    try {
      await this.api.logout();
      this.resetSessionState();
    } catch (error) {
      this.logoutErrorMessage.set(this.errorFrom(error));
      throw error;
    } finally {
      this.logoutLoading.set(false);
    }
  }

  ensureSession(): Promise<void> {
    if (this.sessionInitialized) {
      return Promise.resolve();
    }

    if (this.sessionRestore) {
      return this.sessionRestore;
    }

    this.sessionRestore = this.restoreSession()
      .then(() => {
        this.sessionInitialized = true;
      })
      .finally(() => this.sessionRestore = null);

    return this.sessionRestore;
  }

  private async restoreSession(): Promise<void> {
    await this.api.loadRuntimeConfig();

    if (!this.api.hasToken()) {
      return;
    }

    await this.loadWorkspaces();
    if (!this.api.hasToken()) {
      return;
    }

    const storedWorkspaceId = this.auth.loadWorkspaceId();
    const workspace = selectRestoredWorkspace(this.workspaces(), storedWorkspaceId);

    if (!workspace) {
      return;
    }

    this.setWorkspace(workspace, true);
    await this.refreshWorkspaceData(workspace.id);
  }

  async loadWorkspaces(): Promise<void> {
    if (!this.api.hasToken()) {
      return;
    }

    this.errorMessage.set(null);
    this.workspacesLoading.set(true);

    try {
      const workspaces = await this.api.workspaces();
      this.workspaces.set(workspaces);
    } catch (error) {
      this.workspaces.set([]);
      clearTokenOnUnauthorized(error, () => this.auth.clearToken());
      this.errorMessage.set(this.errorFrom(error));
    } finally {
      this.workspacesLoading.set(false);
    }
  }

  async selectWorkspace(workspace: WorkspaceSummary): Promise<void> {
    this.setWorkspace(workspace, true);
    await this.refreshWorkspaceData(workspace.id);
  }

  async selectCashRegister(cashRegister: PosCashRegister): Promise<void> {
    const selected = this.cashRegisters().find((item) => item.id === cashRegister.id);

    if (!selected) {
      this.errorMessage.set('Selecciona una caja activa del workspace.');
      return;
    }

    if (this.selectedCashRegister()?.id === selected.id) {
      return;
    }

    this.auth.setCashRegisterId(this.selectedWorkspace().id, selected.id);
    this.selectedCashRegister.set(selected);
    this.updateCashSession(selected.currentSession);
    this.cashHistory.set([]);
    this.clearCart();
    await this.refreshWorkspaceData(this.selectedWorkspace().id);
  }

  entryRoute(): string {
    return this.cashRegisterReady() ? this.selectedWorkspace().homeRoute : '/cash-register';
  }

  async setCashScope(scope: CashScope): Promise<void> {
    if (scope === 'pos' && !this.selectedWorkspace().capabilities.pos) {
      scope = 'general';
    }

    if (this.cashScope() === scope && this.cashSession()) return;

    this.cashScope.set(scope);
    this.cashSession.set(null);
    this.cashHistory.set([]);
    await this.loadCashSession();
  }

  setThemeMode(mode: PosThemeMode): void {
    this.workspaceService.setThemeMode(mode);
  }

  setCategory(slug: string): void {
    this.activeCategory.set(slug);
  }

  setSearch(query: string): void {
    this.searchQuery.set(query);
  }

  addToCart(product: PosProduct, quantity = 1, addons: PosProductAddon[] = []): void {
    this.cartService.add(product, quantity, addons);
  }

  increment(lineId: string): void {
    this.cartService.increment(lineId);
  }

  decrement(lineId: string): void {
    this.cartService.decrement(lineId);
  }

  remove(lineId: string): void {
    this.cartService.remove(lineId);
  }

  clearCart(): void {
    this.cartService.clear();
  }

  itemTotal(item: CartItem): number {
    return this.cartService.itemTotal(item);
  }

  money(value: number): string {
    return formatMoney(value, this.selectedWorkspace().currency);
  }

  themeForWorkspace(workspace: WorkspaceSummary): PosTheme {
    return this.workspaceService.themeForWorkspace(workspace);
  }

  async ensureReadyForSale(): Promise<void> {
    if (!this.api.hasToken()) {
      this.applyTheme();
      return;
    }

    if (this.products().length === 0 || this.categories().length <= 1) {
      await this.refreshWorkspaceData(this.selectedWorkspace().id);
    }
  }

  async loadProductDetails(product: PosProduct): Promise<PosProduct> {
    if (!this.api.hasToken()) {
      return product;
    }

    try {
      const detailedProduct = await this.api.product(
        this.selectedWorkspace().id,
        product.uuid ?? product.sku ?? product.id,
      );

      this.products.update((products) =>
        products.map((item) => item.id === detailedProduct.id ? detailedProduct : item),
      );

      return detailedProduct;
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      return product;
    }
  }

  async searchClients(query: string): Promise<PosClient[]> {
    const normalizedQuery = query.trim();

    if (normalizedQuery.length < 2) {
      return [];
    }

    if (!this.api.hasToken()) {
      const lowerQuery = normalizedQuery.toLowerCase();

      return mockRecentOrders
        .map((order, index) => ({
          id: index + 1,
          name: order.customerName,
          email: order.customerEmail,
          phone: order.customerPhone,
          status: 'active',
        }))
        .filter((client, index, clients) =>
          clients.findIndex((item) => item.name === client.name && item.phone === client.phone) === index,
        )
        .filter((client) =>
          client.name.toLowerCase().includes(lowerQuery) ||
          (client.email ?? '').toLowerCase().includes(lowerQuery) ||
          (client.phone ?? '').toLowerCase().includes(lowerQuery),
        );
    }

    try {
      return await this.api.clients(this.selectedWorkspace().id, {
        q: normalizedQuery,
        perPage: 10,
      });
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      return [];
    }
  }

  async createClient(payload: PosCreateClientPayload): Promise<PosClient> {
    if (!this.api.hasToken()) {
      return {
        id: Date.now(),
        name: payload.name || payload.phone || payload.email || 'Cliente',
        email: payload.email,
        phone: payload.phone,
        notes: payload.notes,
        status: 'active',
      };
    }

    try {
      return await this.api.createClient(this.selectedWorkspace().id, payload);
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      throw error;
    }
  }

  async findProductsByScan(code: string): Promise<PosProduct[]> {
    const normalizedCode = code.trim().toLowerCase();

    if (!normalizedCode) {
      return [];
    }

    const localMatches = this.products().filter((product) =>
      product.sku.toLowerCase() === normalizedCode ||
      (product.barcode ?? '').toLowerCase() === normalizedCode,
    );

    if (!this.api.hasToken()) {
      return localMatches;
    }

    try {
      const remoteProducts = await this.api.products(this.selectedWorkspace().id, {
        q: code.trim(),
        perPage: 20,
      });
      const merged = [...localMatches, ...remoteProducts];
      const byId = new Map<number, PosProduct>();

      for (const product of merged) {
        byId.set(product.id, product);
      }

      return Array.from(byId.values()).filter((product) =>
        product.sku.toLowerCase() === normalizedCode ||
        (product.barcode ?? '').toLowerCase() === normalizedCode,
      );
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      return localMatches;
    }
  }

  async completeSale(paymentAmount: number, method: 'cash' | 'transfer', customer?: PosOrderCustomerPayload): Promise<void> {
    if (!this.api.hasToken()) {
      this.completeMockSale(paymentAmount, method, customer);
      return;
    }

    this.errorMessage.set(null);
    this.checkoutLoading.set(true);

    try {
      const total = this.cartSubtotal();
      const amount = Math.max(0, Math.min(paymentAmount, total));
      const payload: PosCreateOrderPayload = {
        customer: this.normalizeCustomer(customer),
        items: this.cartService.toOrderItems(),
        payment: amount > 0
          ? {
              method,
              amount,
              status: 'approved',
            }
          : undefined,
      };

      const order = await this.api.createOrder(this.selectedWorkspace().id, payload);
      this.lastOrder.set(order);
      this.recentOrders.update((orders) => [order, ...orders.filter((item) => item.uuid !== order.uuid)]);
      this.clearCart();
      await this.loadCashSession();
      await this.loadRecentOrders();
      void this.router.navigate(['/pos/recent']);
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      throw error;
    } finally {
      this.checkoutLoading.set(false);
    }
  }

  async saveWithoutPayment(customer?: PosOrderCustomerPayload): Promise<void> {
    await this.completeSale(0, 'cash', customer);
  }

  async addOrderPayment(order: RecentPosOrder, paymentAmount: number, method: 'cash' | 'transfer'): Promise<void> {
    if (!this.api.hasToken()) {
      this.applyMockPayment(order, paymentAmount, method);
      return;
    }

    this.errorMessage.set(null);
    this.checkoutLoading.set(true);

    try {
      const amount = Math.max(0, Math.min(paymentAmount, order.balanceDue));
      const updatedOrder = await this.api.addOrderPayment(this.selectedWorkspace().id, order.uuid, {
        method,
        amount,
        status: 'approved',
      });

      this.lastOrder.set(updatedOrder);
      this.recentOrders.update((orders) =>
        orders.map((item) => item.uuid === updatedOrder.uuid ? updatedOrder : item),
      );
      await this.loadCashSession();
      await this.loadRecentOrders();
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      throw error;
    } finally {
      this.checkoutLoading.set(false);
    }
  }

  async loadCashSession(): Promise<void> {
    if (!this.api.hasToken()) {
      return;
    }

    this.errorMessage.set(null);
    this.cashLoading.set(true);

    try {
      this.updateCashSession(await this.api.cashSession(this.selectedWorkspace().id, this.cashScope()));
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
    } finally {
      this.cashLoading.set(false);
    }
  }

  async loadCashHistory(): Promise<void> {
    if (!this.api.hasToken()) {
      return;
    }

    this.errorMessage.set(null);
    this.cashHistoryLoading.set(true);

    try {
      this.cashHistory.set(await this.api.cashSessions(this.selectedWorkspace().id, {
        status: 'closed',
        perPage: 30,
        scope: this.cashScope(),
      }));
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
    } finally {
      this.cashHistoryLoading.set(false);
    }
  }

  async openCashSession(payload: PosOpenCashSessionPayload): Promise<void> {
    if (!this.api.hasToken()) {
      this.updateCashSession(this.mockCashSession(payload.openingCashAmount));
      return;
    }

    this.errorMessage.set(null);
    this.cashLoading.set(true);

    try {
      this.updateCashSession(await this.api.openCashSession(this.selectedWorkspace().id, payload, this.cashScope()));
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      throw error;
    } finally {
      this.cashLoading.set(false);
    }
  }

  async addCashMovement(payload: PosCashMovementPayload): Promise<void> {
    const session = this.cashSession();

    if (!session) {
      this.errorMessage.set('Abre caja antes de registrar movimientos.');
      return;
    }

    if (!this.api.hasToken()) {
      this.updateCashSession(this.mockMovement(session, payload));
      return;
    }

    this.errorMessage.set(null);
    this.cashLoading.set(true);

    try {
      this.updateCashSession(await this.api.addCashMovement(this.selectedWorkspace().id, session.uuid, payload, this.cashScope()));
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      throw error;
    } finally {
      this.cashLoading.set(false);
    }
  }

  async closeCashSession(payload: PosCloseCashSessionPayload): Promise<void> {
    const session = this.cashSession();

    if (!session) {
      this.errorMessage.set('No hay caja abierta para cerrar.');
      return;
    }

    if (!this.api.hasToken()) {
      const closedSession = this.mockClosedCashSession(session, payload);

      this.cashHistory.update((sessions) => [closedSession, ...sessions]);
      this.updateCashSession(null);
      return;
    }

    this.errorMessage.set(null);
    this.cashLoading.set(true);

    try {
      const closedSession = await this.api.closeCashSession(this.selectedWorkspace().id, session.uuid, payload, this.cashScope());

      this.cashHistory.update((sessions) => [closedSession, ...sessions.filter((item) => item.uuid !== closedSession.uuid)]);
      this.updateCashSession(null);
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
      throw error;
    } finally {
      this.cashLoading.set(false);
    }
  }

  async loadRecentOrders(sources: CommercialOrderSource[] = this.recentSources()): Promise<void> {
    const selectedSources: CommercialOrderSource[] = sources.length > 0 ? sources : ['pos'];
    this.recentSources.set(selectedSources);

    if (!this.api.hasToken()) {
      this.recentOrders.set(mockRecentOrders.filter((order) => selectedSources.includes(order.source)));
      return;
    }

    this.errorMessage.set(null);
    this.recentLoading.set(true);

    try {
      this.recentOrders.set(await this.api.orders(this.selectedWorkspace().id, { sources: selectedSources }));
    } catch (error) {
      this.errorMessage.set(this.errorFrom(error));
    } finally {
      this.recentLoading.set(false);
    }
  }

  async setRecentSource(source: CommercialOrderSource, enabled: boolean): Promise<void> {
    const current = this.recentSources();
    const next = enabled
      ? Array.from(new Set([...current, source]))
      : current.filter((item) => item !== source);

    await this.loadRecentOrders(next.length > 0 ? next : ['pos']);
  }

  completeMockSale(paymentAmount: number, method: 'cash' | 'transfer', customer?: PosOrderCustomerPayload): void {
    const total = this.cartSubtotal();
    const paidTotal = Math.min(paymentAmount, total);
    const balanceDue = Math.max(total - paidTotal, 0);
    const paid = balanceDue === 0;
      const order: RecentPosOrder = {
        uuid: crypto.randomUUID(),
        folio: `PED-2026-${String(this.recentOrders().length + 44).padStart(5, '0')}`,
        customerName: customer?.name?.trim() || 'Publico en general',
        customerEmail: customer?.email?.trim() || undefined,
        customerPhone: customer?.phone?.trim() || undefined,
        source: 'pos',
      status: paid ? 'confirmed' : 'quoted',
      paymentStatus: paid ? 'paid' : paidTotal > 0 ? 'partial' : 'unpaid',
      total,
      paidTotal,
      balanceDue,
      method,
      createdAt: 'Ahora',
      items: this.cart().map((item, index) => ({
        id: index + 1,
        name: item.product.name,
        quantity: item.quantity,
        unitPrice: item.product.price,
        total: this.itemTotal(item),
        productId: item.product.id,
        productUuid: item.product.uuid,
        addons: item.addons.map((addon) => ({
          name: addon.name,
          priceDelta: addon.priceDelta,
        })),
      })),
      payments: paidTotal > 0
        ? [{
            id: 1,
            type: paid ? 'payment' : 'deposit',
            method,
            amount: paidTotal,
            status: 'approved',
          }]
        : [],
    };

    this.lastOrder.set(order);
    this.recentOrders.update((orders) => [order, ...orders]);
    this.clearCart();
    void this.router.navigate(['/pos/recent']);
  }

  private normalizeCustomer(customer?: PosOrderCustomerPayload): PosOrderCustomerPayload | undefined {
    if (!customer) {
      return undefined;
    }

    const normalized = {
      name: customer.name?.trim() || undefined,
      email: customer.email?.trim() || undefined,
      phone: customer.phone?.trim() || undefined,
      notes: customer.notes?.trim() || undefined,
    };

    return Object.values(normalized).some(Boolean) ? normalized : undefined;
  }

  private applyMockPayment(order: RecentPosOrder, paymentAmount: number, method: 'cash' | 'transfer'): void {
    const amount = Math.max(0, Math.min(paymentAmount, order.balanceDue));
    const paidTotal = order.paidTotal + amount;
    const balanceDue = Math.max(order.total - paidTotal, 0);
    const updatedOrder: RecentPosOrder = {
      ...order,
      paidTotal,
      balanceDue,
      paymentStatus: balanceDue <= 0 ? 'paid' : paidTotal > 0 ? 'partial' : 'unpaid',
      status: balanceDue <= 0 ? 'confirmed' : order.status,
      method,
    };

    this.recentOrders.update((orders) =>
      orders.map((item) => item.uuid === order.uuid ? updatedOrder : item),
    );
    this.lastOrder.set(updatedOrder);
  }

  private mockCashSession(openingCashAmount: number): PosCashSession {
    return {
      id: 1,
      uuid: crypto.randomUUID(),
      folio: `CAJA-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-DEMO`,
      status: 'open',
      openedAt: new Date().toISOString(),
      openingCashAmount,
      expectedCashAmount: openingCashAmount,
      expectedTransferAmount: 0,
      expectedTotal: openingCashAmount,
      differenceAmount: 0,
      movements: openingCashAmount > 0
        ? [{
            id: 1,
            uuid: crypto.randomUUID(),
            type: 'opening',
            method: 'cash',
            amount: openingCashAmount,
            currency: this.selectedWorkspace().currency,
            description: 'Fondo inicial de caja',
            occurredAt: new Date().toISOString(),
          }]
        : [],
    };
  }

  private mockMovement(session: PosCashSession, payload: PosCashMovementPayload): PosCashSession {
    const amount = ['expense', 'withdrawal'].includes(payload.type) ? payload.amount * -1 : payload.amount;
    const movements = [
      {
        id: session.movements.length + 1,
        uuid: crypto.randomUUID(),
        type: payload.type,
        method: payload.method,
        amount,
        currency: this.selectedWorkspace().currency,
        description: payload.description,
        reference: payload.reference,
        occurredAt: new Date().toISOString(),
      },
      ...session.movements,
    ];
    const expectedCashAmount = movements
      .filter((movement) => movement.method === 'cash')
      .reduce((total, movement) => total + movement.amount, 0);
    const expectedTransferAmount = movements
      .filter((movement) => movement.method === 'transfer')
      .reduce((total, movement) => total + movement.amount, 0);

    return {
      ...session,
      movements,
      expectedCashAmount,
      expectedTransferAmount,
      expectedTotal: expectedCashAmount + expectedTransferAmount,
    };
  }

  private mockClosedCashSession(session: PosCashSession, payload: PosCloseCashSessionPayload): PosCashSession {
    const countedCashAmount = payload.countedCashAmount;
    const countedTransferAmount = payload.countedTransferAmount;
    const countedTotal = countedCashAmount + countedTransferAmount;

    return {
      ...session,
      status: 'closed',
      closedAt: new Date().toISOString(),
      countedCashAmount,
      countedTransferAmount,
      differenceAmount: countedTotal - session.expectedTotal,
      notes: payload.notes ?? session.notes,
    };
  }

  private async refreshWorkspaceData(workspaceId: number): Promise<void> {
    if (!this.api.hasToken()) {
      this.applyTheme();
      return;
    }

    this.errorMessage.set(null);
    this.catalogLoading.set(true);

    try {
      await this.refreshCashRegisters(workspaceId);

      if (!this.selectedCashRegister()) {
        this.categories.set(this.withTodos([]));
        this.products.set([]);
        this.recentOrders.set([]);
        this.cashSession.set(null);
        this.cashHistory.set([]);

        return;
      }

      const config = await this.api.config(workspaceId);
      this.setWorkspace(config.workspace, false);
      if (config.workspace.capabilities.pos) {
        this.categories.set(this.withTodos(config.categories));
        this.products.set(await this.api.products(workspaceId));
        await this.loadRecentOrders();
      } else {
        this.categories.set(this.withTodos([]));
        this.products.set([]);
        this.recentOrders.set([]);
      }
      if (config.workspace.capabilities.cash) await this.loadCashSession();
    } catch (error) {
      clearTokenOnUnauthorized(error, () => this.auth.clearToken());
      this.errorMessage.set(this.errorFrom(error));
    } finally {
      this.catalogLoading.set(false);
    }
  }

  private async refreshCashRegisters(workspaceId: number): Promise<void> {
    this.cashRegistersLoading.set(true);

    try {
      const response = await this.api.cashRegisters(workspaceId);
      const storedCashRegisterId = this.auth.loadCashRegisterId(workspaceId);
      const selected = response.cashRegisters.find((item) => item.id === storedCashRegisterId)
        ?? (response.cashRegisters.length === 1 ? response.cashRegisters[0] : null);

      this.cashRegisters.set(response.cashRegisters);
      this.selectedCashRegister.set(selected);
      this.cashSession.set(selected?.currentSession ?? null);

      if (selected) {
        this.auth.setCashRegisterId(workspaceId, selected.id);
      } else {
        this.auth.clearCashRegisterId(workspaceId);
      }
    } finally {
      this.cashRegistersLoading.set(false);
    }
  }

  private updateCashSession(session: PosCashSession | null): void {
    this.cashSession.set(session);

    const selected = this.selectedCashRegister();

    if (!selected) {
      return;
    }

    const updated = { ...selected, currentSession: session };
    this.selectedCashRegister.set(updated);
    this.cashRegisters.update((registers) =>
      registers.map((register) => register.id === updated.id ? updated : register),
    );
  }

  private resetSessionState(): void {
    this.sessionRestore = null;
    this.sessionInitialized = false;
    this.workspaces.set([]);
    this.categories.set(this.withTodos([]));
    this.products.set([]);
    this.recentOrders.set([]);
    this.recentSources.set(['pos']);
    this.activeCategory.set('todos');
    this.searchQuery.set('');
    this.lastOrder.set(null);
    this.clearCart();
    this.cashScope.set('pos');
    this.cashSession.set(null);
    this.cashHistory.set([]);
    this.cashRegisters.set([]);
    this.selectedCashRegister.set(null);
    this.errorMessage.set(null);
  }

  private setWorkspace(workspace: WorkspaceSummary, resetCashRegister: boolean): void {
    this.selectedWorkspace.set(workspace);
    this.auth.setWorkspaceId(workspace.id);
    this.activeCategory.set('todos');
    this.searchQuery.set('');
    this.clearCart();
    this.cashScope.set(workspace.capabilities.pos ? 'pos' : 'general');
    this.cashSession.set(null);
    this.cashHistory.set([]);
    if (resetCashRegister) {
      this.cashRegisters.set([]);
      this.selectedCashRegister.set(null);
    }
    this.applyTheme();
  }

  private withTodos(categories: PosCategory[]): PosCategory[] {
    return [
      {
        id: 0,
        name: 'Todos',
        slug: 'todos',
      },
      ...categories,
    ];
  }

  private errorFrom(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const message = this.validationMessage(error.error);

      return message || `No se pudo completar la operación (${error.status}).`;
    }

    return 'No se pudo completar la operación.';
  }

  private validationMessage(payload: unknown): string | null {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const data = payload as Record<string, unknown>;

    if (typeof data['message'] === 'string' && data['message'].length > 0) {
      return data['message'];
    }

    const errors = data['errors'];

    if (!errors || typeof errors !== 'object') {
      return null;
    }

    const firstError = Object.values(errors as Record<string, unknown>)[0];

    if (Array.isArray(firstError) && typeof firstError[0] === 'string') {
      return firstError[0];
    }

    return null;
  }
}
